import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Cart Discount Component
 *
 * Handles:
 * - Applying discount codes
 * - Removing discount codes
 * - Shipping discount validation
 * - Cart section rendering
 * - Cart update events
 * - Request cancellation
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError
 * @property {HTMLElement} cartDiscountErrorDiscountCode
 * @property {HTMLElement} cartDiscountErrorShipping
 *
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Aborts the previous request and creates a fresh controller.
   *
   * @returns {AbortController}
   */
  #createAbortController() {
    this.#activeFetch?.abort();

    const controller = new AbortController();
    this.#activeFetch = controller;

    return controller;
  }

  /**
   * Applies a discount code.
   *
   * @param {SubmitEvent} event
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (typeof this.dataset.sectionId !== 'string') return;

    const discountInput = form.querySelector('input[name="discount"]');

    if (!(discountInput instanceof HTMLInputElement)) return;

    const discountCodeValue = discountInput.value.trim();

    if (!discountCodeValue) return;

    const existingDiscounts = this.#existingDiscounts();

    /*
     * Avoid submitting the same code twice.
     * Comparison is case-insensitive for a cleaner UX.
     */
    const alreadyApplied = existingDiscounts.some(
      (code) => code.toLowerCase() === discountCodeValue.toLowerCase()
    );

    if (alreadyApplied) {
      discountInput.value = '';
      return;
    }

    this.#hideErrors();

    const abortController = this.#createAbortController();

    const allDiscountCodes = [
      ...existingDiscounts,
      discountCodeValue,
    ];

    const deferredPromise =
      CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({
          code,
        })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(
        Theme.routes.cart_update_url,
        {
          ...config,
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Discount request failed with status ${response.status}`
        );
      }

      const data = await response.json();

      const ajaxCart =
        CartDiscountUpdateEvent.createCartFromAjaxResponse(data);

      const submittedDiscount = data.discount_codes?.find(
        (discount) =>
          discount.code?.toLowerCase() ===
          discountCodeValue.toLowerCase()
      );

      /*
       * Invalid / non-applicable discount.
       */
      if (submittedDiscount?.applicable === false) {
        discountInput.value = '';

        this.#handleDiscountError('discount_code');

        deferredPromise.resolve({
          cart: ajaxCart,
        });

        return;
      }

      const newHtml =
        data.sections?.[this.dataset.sectionId];

      if (typeof newHtml !== 'string') {
        throw new Error(
          'Cart discount section HTML was not returned.'
        );
      }

      const parsedHtml = new DOMParser().parseFromString(
        newHtml,
        'text/html'
      );

      const section = parsedHtml.getElementById(
        `shopify-section-${this.dataset.sectionId}`
      );

      if (section) {
        const discountPills =
          section.querySelectorAll(
            '.cart-discount__pill'
          );

        const renderedCodes = Array.from(discountPills)
          .map((element) =>
            element instanceof HTMLLIElement
              ? element.dataset.discountCode
              : null
          )
          .filter(
            /** @returns {code is string} */
            (code) => Boolean(code)
          );

        /*
         * Shopify can report a shipping discount as applicable
         * even though it does not render as a standard discount pill.
         */
        const sameRenderedDiscounts =
          renderedCodes.length === existingDiscounts.length &&
          renderedCodes.every((code) =>
            existingDiscounts.includes(code)
          );

        if (
          sameRenderedDiscounts &&
          submittedDiscount?.applicable === true
        ) {
          this.#handleDiscountError('shipping');

          discountInput.value = '';

          deferredPromise.resolve({
            cart: ajaxCart,
          });

          return;
        }
      }

      deferredPromise.resolve({
        cart: ajaxCart,
      });

      /*
       * The discount input may use data-skip-node-update,
       * so clear it explicitly before morphing.
       */
      discountInput.value = '';

      morphSection(
        this.dataset.sectionId,
        newHtml,
        {
          mode: this.closest('theme-drawer')
            ? 'hydration'
            : 'full',
        }
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'AbortError'
      ) {
        return;
      }

      deferredPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error instanceof Error
              ? error.message
              : 'Failed to apply discount',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      /*
       * Don't clear a newer request's controller.
       */
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }

      cartPerformance.measureFromEvent(
        'discount-update:user-action',
        event
      );
    }
  };

  /**
   * Removes an applied discount.
   *
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscount = async (event) => {
    /*
     * Allow:
     * - Mouse click
     * - Enter
     * - Space
     */
    if (
      event instanceof KeyboardEvent &&
      event.key !== 'Enter' &&
      event.key !== ' '
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!(event.target instanceof HTMLElement)) return;
    if (typeof this.dataset.sectionId !== 'string') return;

    const pill = event.target.closest(
      '.cart-discount__pill'
    );

    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;

    if (!discountCode) return;

    const existingDiscounts =
      this.#existingDiscounts();

    const index = existingDiscounts.findIndex(
      (code) =>
        code.toLowerCase() === discountCode.toLowerCase()
    );

    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController =
      this.#createAbortController();

    const deferredPromise =
      CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map(
          (code) => ({ code })
        ),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(
        Theme.routes.cart_update_url,
        {
          ...config,
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Discount removal failed with status ${response.status}`
        );
      }

      const data = await response.json();

      deferredPromise.resolve({
        cart:
          CartDiscountUpdateEvent.createCartFromAjaxResponse(
            data
          ),
      });

      const newHtml =
        data.sections?.[this.dataset.sectionId];

      if (typeof newHtml !== 'string') {
        throw new Error(
          'Cart discount section HTML was not returned.'
        );
      }

      morphSection(
        this.dataset.sectionId,
        newHtml,
        {
          mode: this.closest('theme-drawer')
            ? 'hydration'
            : 'full',
        }
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'AbortError'
      ) {
        return;
      }

      deferredPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error instanceof Error
              ? error.message
              : 'Failed to remove discount',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  };

  /**
   * Hides all discount validation messages.
   */
  #hideErrors() {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
  }

  /**
   * Displays a discount error.
   *
   * @param {'discount_code' | 'shipping'} type
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    const target =
      type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : cartDiscountErrorShipping;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage =
      type === 'discount_code'
        ? 'Invalid discount code'
        : 'Discount not applicable for shipping';

    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns currently applied discount codes.
   *
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];

    const discountPills =
      this.querySelectorAll(
        '.cart-discount__pill'
      );

    for (const pill of discountPills) {
      if (!(pill instanceof HTMLLIElement)) continue;

      const code = pill.dataset.discountCode;

      if (!code) continue;

      discountCodes.push(code);
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define(
    'cart-discount-component',
    CartDiscount
  );
}